import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Breadcrumbs } from '../components/Breadcrumbs'
import { YouTubeEmbed } from '../components/YouTubeEmbed'
import {
  DT_EPISODES,
  DT_FILTERS,
  DT_SERIES,
  type DtEpisode,
  type DtFilter,
} from '../data/deiveegamTheriyuma'
import { SITE } from '../data/site'
import { useSeo } from '../hooks/useSeo'
import './DeiveegamTheriyuma.css'

function thumbUrl(videoId: string) {
  return `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`
}

function formatPublished(iso: string) {
  const d = new Date(`${iso}T12:00:00`)
  return d.toLocaleDateString('ta-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

function EpisodeCard({
  episode,
  open,
  onToggle,
}: {
  episode: DtEpisode
  open: boolean
  onToggle: (id: string, next: boolean) => void
}) {
  const detailsRef = useRef<HTMLDetailsElement>(null)
  const ytHref = `https://www.youtube.com/watch?v=${episode.youtubeVideoId}`

  useEffect(() => {
    const el = detailsRef.current
    if (!el) return
    el.open = open
  }, [open])

  return (
    <article className="dt-card" id={episode.anchor}>
      <div className="dt-card__media">
        <span className="dt-card__ep">பகுதி {String(episode.number).padStart(2, '0')}</span>
        <img
          src={thumbUrl(episode.youtubeVideoId)}
          alt={episode.titleTa}
          width={640}
          height={360}
          loading="lazy"
          decoding="async"
          onError={(e) => {
            const img = e.currentTarget
            if (!img.src.includes('hqdefault')) {
              img.src = `https://i.ytimg.com/vi/${episode.youtubeVideoId}/hqdefault.jpg`
            }
          }}
        />
      </div>

      <div className="dt-card__body">
        <p className="dt-card__tag">{episode.cardTag}</p>
        <h2 className="dt-card__title">{episode.titleTa}</h2>
        <p className="dt-card__hook">{episode.hookTa}</p>
        <p className="dt-card__date">வெளியீடு: {formatPublished(episode.published)}</p>

        <div className="dt-card__actions">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => onToggle(episode.id, true)}
          >
            வீடியோ பார்க்க
          </button>
          <a className="btn btn--outline" href={`#${episode.anchor}`} onClick={() => onToggle(episode.id, true)}>
            மேலும் படிக்க
          </a>
        </div>

        <details
          ref={detailsRef}
          className="dt-card__details"
          onToggle={(e) => onToggle(episode.id, e.currentTarget.open)}
        >
          <summary className="dt-card__summary">விளக்கம் & முக்கிய குறிப்பு</summary>

          <div className="dt-card__expand">
            {/* Body copy always in DOM for indexing; embed mounts only when open (lazy, no autoplay). */}
            <p className="dt-card__copy">{episode.bodyTa}</p>

            {open && <YouTubeEmbed videoId={episode.youtubeVideoId} title={episode.titleTa} />}
            {!open && (
              <p className="dt-card__yt-hint">
                <a href={ytHref} target="_blank" rel="noopener noreferrer">
                  YouTube-ல் பார்க்க
                </a>
              </p>
            )}

            {episode.noteTa && (
              <aside className="dt-card__note" aria-label="முக்கிய குறிப்பு">
                <strong>முக்கிய குறிப்பு</strong>
                <p>{episode.noteTa}</p>
              </aside>
            )}

            {episode.related.length > 0 && (
              <div className="dt-card__related">
                <p className="dt-card__related-label">தொடர்புடைய பக்கங்கள்</p>
                <ul>
                  {episode.related.map((link) => (
                    <li key={link.to + link.label}>
                      <Link to={link.to}>{link.label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="dt-card__yt-link">
              <a href={ytHref} target="_blank" rel="noopener noreferrer">
                YouTube-ல் திறக்க →
              </a>
            </p>
          </div>
        </details>
      </div>
    </article>
  )
}

export function DeiveegamTheriyumaPage() {
  const { hash } = useLocation()
  const [filter, setFilter] = useState<DtFilter | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)

  const jsonLd = useMemo(() => {
    const pageUrl = `https://${SITE.domain}/deiveegam-theriyuma/`
    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'CollectionPage',
          '@id': `${pageUrl}#collection`,
          name: DT_SERIES.h1,
          description: DT_SERIES.seoDescription,
          url: pageUrl,
          inLanguage: 'ta',
          isPartOf: { '@type': 'WebSite', name: SITE.nameEn, url: `https://${SITE.domain}/` },
          mainEntity: { '@id': `${pageUrl}#itemlist` },
        },
        {
          '@type': 'ItemList',
          '@id': `${pageUrl}#itemlist`,
          name: DT_SERIES.heroTitle,
          numberOfItems: DT_EPISODES.length,
          itemListOrder: 'https://schema.org/ItemListOrderDescending',
          itemListElement: DT_EPISODES.map((ep, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            url: `${pageUrl}#${ep.anchor}`,
            item: { '@id': `${pageUrl}#video-${ep.id}` },
          })),
        },
        ...DT_EPISODES.map((ep) => ({
          '@type': 'VideoObject',
          '@id': `${pageUrl}#video-${ep.id}`,
          name: ep.titleTa,
          description: ep.hookTa,
          thumbnailUrl: thumbUrl(ep.youtubeVideoId),
          uploadDate: ep.published,
          embedUrl: `https://www.youtube.com/embed/${ep.youtubeVideoId}`,
          contentUrl: `https://www.youtube.com/watch?v=${ep.youtubeVideoId}`,
          inLanguage: 'ta',
        })),
      ],
    }
  }, [])

  useSeo({
    title: DT_SERIES.seoTitle,
    description: DT_SERIES.seoDescription,
    path: '/deiveegam-theriyuma/',
    jsonLd,
    skipTitleSuffix: true,
  })

  const visible = useMemo(
    () => (filter ? DT_EPISODES.filter((ep) => ep.filters.includes(filter)) : DT_EPISODES),
    [filter],
  )

  useEffect(() => {
    const raw = hash.replace(/^#/, '')
    if (!raw) return
    const ep = DT_EPISODES.find((e) => e.anchor === raw || e.id === raw)
    if (!ep) return
    setOpenId(ep.id)
    requestAnimationFrame(() => {
      document.getElementById(ep.anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }, [hash])

  function onToggle(id: string, next: boolean) {
    setOpenId(next ? id : openId === id ? null : openId)
    if (next) {
      const ep = DT_EPISODES.find((e) => e.id === id)
      if (ep && window.location.hash !== `#${ep.anchor}`) {
        window.history.replaceState(null, '', `#${ep.anchor}`)
      }
    }
  }

  return (
    <div className="dt-hub">
      <section className="dt-hero" aria-labelledby="dt-hero-title">
        <div className="dt-hero__ornament" aria-hidden="true" />
        <div className="container dt-hero__inner">
          <Breadcrumbs items={[{ label: 'முகப்பு', to: '/' }, { label: DT_SERIES.heroTitle }]} />
          <p className="dt-hero__eyebrow">தெய்வீக ஓசை குறுந்தகவல் தொடர்</p>
          <h1 id="dt-hero-title" className="dt-hero__title">
            {DT_SERIES.h1}
          </h1>
          <p className="dt-hero__support">{DT_SERIES.heroSupport}</p>
        </div>
      </section>

      <div className="container section dt-hub__main">
        <section className="dt-intro" aria-labelledby="dt-intro-heading">
          <h2 id="dt-intro-heading" className="sr-only">
            தொடர் அறிமுகம்
          </h2>
          <p className="dt-intro__text">{DT_SERIES.introTa}</p>
          <aside className="dt-disclaimer" role="note">
            <strong>பொறுப்புத்துறப்பு</strong>
            <p>{DT_SERIES.disclaimerTa}</p>
          </aside>
        </section>

        <div className="dt-filters" role="group" aria-label="வகை வடிகட்டி">
          <button
            type="button"
            className={!filter ? 'dt-chip is-active' : 'dt-chip'}
            onClick={() => setFilter(null)}
          >
            அனைத்தும்
          </button>
          {DT_FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              className={filter === f ? 'dt-chip is-active' : 'dt-chip'}
              onClick={() => setFilter(filter === f ? null : f)}
            >
              {f}
            </button>
          ))}
        </div>

        <p className="dt-count">
          {visible.length} பகுதிகள் · புதியவை முதலில்
        </p>

        <div className="dt-grid">
          {visible.map((episode) => (
            <EpisodeCard
              key={episode.id}
              episode={episode}
              open={openId === episode.id}
              onToggle={onToggle}
            />
          ))}
        </div>

        {visible.length === 0 && (
          <div className="empty-state">
            <p>இந்த வகையில் பகுதிகள் இல்லை.</p>
            <button type="button" className="btn btn--primary" onClick={() => setFilter(null)}>
              அனைத்தையும் காட்டு
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
